import React, { useEffect, useState } from 'react';
import { Modal, message } from 'antd';
import {
  CCC_CLIENT_TYPE_COLORS,
  CCC_CLIENT_TYPE_LABELS,
  type CccClientType,
} from '../config/cccClientRoutes';
import cccCalendarApi, {
  CccCalendarClientOption,
  CccCalendarEvent,
  CccCalendarEventInput,
} from '../api/cccCalendarApi';
import {
  formatTimeLabel,
  fromDatetimeLocalValue,
  toDatetimeLocalValue,
} from '../utils/cccCalendarWeek';

type Props = {
  open: boolean;
  initialStart: Date;
  initialEnd: Date;
  event?: CccCalendarEvent | null;
  /** Pre-fill and lock client (e.g. from Recuperação lead panel). */
  initialClient?: CccCalendarClientOption | null;
  lockClient?: boolean;
  defaultTitle?: string;
  onClose: () => void;
  onSaved: () => void;
};

const CLIENT_TYPES: CccClientType[] = [
  'none',
  'recuperacao',
  'prospeccao',
  'retencao',
  'resolucao',
];

const CccCalendarEventModal: React.FC<Props> = ({
  open,
  initialStart,
  initialEnd,
  event,
  initialClient = null,
  lockClient = false,
  defaultTitle = '',
  onClose,
  onSaved,
}) => {
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [startLocal, setStartLocal] = useState('');
  const [endLocal, setEndLocal] = useState('');
  const [clientType, setClientType] = useState<CccClientType>('none');
  const [clientSearch, setClientSearch] = useState('');
  const [clientOptions, setClientOptions] = useState<CccCalendarClientOption[]>([]);
  const [selectedClient, setSelectedClient] = useState<CccCalendarClientOption | null>(null);
  const [saving, setSaving] = useState(false);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (event) {
      setTitle(event.title);
      setNotes(event.notes || '');
      setStartLocal(toDatetimeLocalValue(new Date(event.start_at.replace('Z', ''))));
      setEndLocal(toDatetimeLocalValue(new Date(event.end_at.replace('Z', ''))));
      setClientType(event.client_type || 'none');
      if (event.client_ref && event.client_label) {
        const snap = (event.client_snapshot || {}) as Record<string, unknown>;
        const subtitle = [
          snap.nome_responsavel,
          snap.contacto,
          snap.nif ? `NIF ${snap.nif}` : null,
          snap.email,
        ]
          .filter(Boolean)
          .join(' · ');
        setSelectedClient({
          client_type: event.client_type,
          client_ref: event.client_ref,
          label: event.client_label,
          subtitle: subtitle || undefined,
          route_path:
            event.client_type === 'retencao'
              ? '/retencao'
              : event.client_type === 'prospeccao'
                ? '/prospeccao'
                : event.client_type === 'resolucao'
                  ? '/resolucao'
                  : '/recuperacao',
          snapshot: event.client_snapshot || undefined,
        });
      } else {
        setSelectedClient(null);
      }
    } else {
      const preset = lockClient && initialClient ? initialClient : null;
      setTitle(defaultTitle || (preset ? `Contacto — ${preset.label}` : ''));
      setNotes('');
      setStartLocal(toDatetimeLocalValue(initialStart));
      setEndLocal(toDatetimeLocalValue(initialEnd));
      if (preset) {
        setClientType(preset.client_type || 'none');
        setSelectedClient(preset);
      } else {
        setClientType('none');
        setSelectedClient(null);
      }
    }
    setClientSearch('');
    setClientOptions([]);
  }, [open, event, initialStart, initialEnd, initialClient, lockClient, defaultTitle]);

  useEffect(() => {
    if (!open || lockClient || clientType !== 'recuperacao') {
      setClientOptions([]);
      return;
    }
    const t = window.setTimeout(async () => {
      setSearching(true);
      try {
        const res = await cccCalendarApi.searchClients(clientSearch, 'recuperacao');
        setClientOptions(res.clients || []);
      } catch {
        setClientOptions([]);
      } finally {
        setSearching(false);
      }
    }, 280);
    return () => window.clearTimeout(t);
  }, [open, clientType, clientSearch]);

  const buildPayload = (): CccCalendarEventInput | null => {
    const start = fromDatetimeLocalValue(startLocal);
    const end = fromDatetimeLocalValue(endLocal);
    if (!title.trim()) {
      message.warning('Indique um título para o evento');
      return null;
    }
    if (!start || !end || end <= start) {
      message.warning('Verifique o intervalo de horas');
      return null;
    }
    const pad = (n: number) => n.toString().padStart(2, '0');
    const toIso = (d: Date) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
        d.getMinutes()
      )}:00Z`;

    const type = clientType;
    const snap = selectedClient?.snapshot;
    return {
      title: title.trim(),
      notes: notes.trim() || undefined,
      start_at: toIso(start),
      end_at: toIso(end),
      color: CCC_CLIENT_TYPE_COLORS[type],
      client_type: type,
      client_ref: type === 'none' ? undefined : selectedClient?.client_ref,
      client_label: type === 'none' ? undefined : selectedClient?.label,
      client_snapshot: type === 'none' ? undefined : snap,
    };
  };

  const handleSave = async () => {
    const payload = buildPayload();
    if (!payload) return;
    setSaving(true);
    try {
      if (event?.id) {
        const res = await cccCalendarApi.updateEvent(event.id, payload);
        if (!res.success) {
          message.error(res.message || 'Não foi possível guardar');
          return;
        }
        message.success('Evento atualizado');
      } else {
        const res = await cccCalendarApi.createEvent(payload);
        if (!res.success) {
          message.error(res.message || 'Não foi possível criar o evento');
          return;
        }
        message.success('Evento criado');
      }
      onSaved();
      onClose();
    } catch {
      message.error('Erro ao guardar evento');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!event?.id) return;
    setSaving(true);
    try {
      await cccCalendarApi.deleteEvent(event.id);
      message.success('Evento eliminado');
      onSaved();
      onClose();
    } catch {
      message.error('Não foi possível eliminar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={event ? 'Editar evento' : lockClient ? 'Criar evento' : 'Novo evento'}
      visible={open}
      onCancel={onClose}
      onOk={handleSave}
      okText={event ? 'Guardar' : 'Criar'}
      cancelText="Cancelar"
      confirmLoading={saving}
      width={520}
      destroyOnClose
      footer={
        <div className="ccc-cal-modal-footer">
          {event ? (
            <button type="button" className="ccc-cal-btn ccc-cal-btn--danger" onClick={handleDelete} disabled={saving}>
              Eliminar
            </button>
          ) : (
            <span />
          )}
          <div className="ccc-cal-modal-footer-actions">
            <button type="button" className="ccc-cal-btn ccc-cal-btn--ghost" onClick={onClose}>
              Cancelar
            </button>
            <button type="button" className="ccc-cal-btn ccc-cal-btn--primary" onClick={handleSave} disabled={saving}>
              {event ? 'Guardar' : 'Criar'}
            </button>
          </div>
        </div>
      }
    >
      <div className="ccc-cal-form">
        <p className="ccc-cal-hint">
          Recebe um aviso 1 hora, 30 minutos e 5 minutos antes deste evento.
        </p>
        <label className="ccc-cal-label">
          Título
          <input
            className="ccc-cal-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ex.: Chamada de follow-up"
          />
        </label>
        <div className="ccc-cal-row">
          <label className="ccc-cal-label">
            Início
            <input
              type="datetime-local"
              className="ccc-cal-input"
              value={startLocal}
              onChange={(e) => setStartLocal(e.target.value)}
            />
          </label>
          <label className="ccc-cal-label">
            Fim
            <input
              type="datetime-local"
              className="ccc-cal-input"
              value={endLocal}
              onChange={(e) => setEndLocal(e.target.value)}
            />
          </label>
        </div>
        <label className="ccc-cal-label">
          Notas
          <textarea
            className="ccc-cal-input ccc-cal-textarea"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Opcional"
          />
        </label>
        {lockClient && selectedClient ? (
          <div className="ccc-cal-client-locked">
            <span className="ccc-cal-client-locked-label">Cliente associado</span>
            <strong>{selectedClient.label}</strong>
            {selectedClient.subtitle && <span className="ccc-cal-client-locked-sub">{selectedClient.subtitle}</span>}
          </div>
        ) : (
          <label className="ccc-cal-label">
            Área do cliente
            <select
              className="ccc-cal-input"
              value={clientType}
              onChange={(e) => {
                const t = e.target.value as CccClientType;
                setClientType(t);
                setSelectedClient(null);
              }}
            >
              {CLIENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {CCC_CLIENT_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </label>
        )}
        {!lockClient && selectedClient && clientType !== 'none' && (
          <div className="ccc-cal-client-locked">
            <span className="ccc-cal-client-locked-label">Cliente associado</span>
            <strong>{selectedClient.label}</strong>
            {selectedClient.subtitle && (
              <span className="ccc-cal-client-locked-sub">{selectedClient.subtitle}</span>
            )}
            {clientType === 'recuperacao' && (
              <button type="button" className="ccc-cal-link" onClick={() => setSelectedClient(null)}>
                Remover
              </button>
            )}
          </div>
        )}
        {!lockClient && clientType === 'recuperacao' && !selectedClient && (
          <div className="ccc-cal-client-picker">
            <label className="ccc-cal-label">
              Cliente (Recuperação)
              <input
                className="ccc-cal-input"
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                placeholder="Pesquisar nas suas marcações iniciadas…"
              />
            </label>
            <ul className="ccc-cal-client-list">
              {searching && <li className="ccc-cal-client-empty">A pesquisar…</li>}
              {!searching && clientOptions.length === 0 && (
                <li className="ccc-cal-client-empty">
                  {clientSearch.trim()
                    ? 'Sem resultados nas suas marcações iniciadas'
                    : 'Inicie marcações em Recuperação para as associar ao calendário'}
                </li>
              )}
              {clientOptions.map((c) => (
                <li key={c.client_ref}>
                  <button
                    type="button"
                    className="ccc-cal-client-option"
                    onClick={() => {
                      setSelectedClient(c);
                      if (!title.trim()) setTitle(`Contacto — ${c.label}`);
                    }}
                  >
                    <span className="ccc-cal-client-option-title">{c.label}</span>
                    {c.subtitle && <span className="ccc-cal-client-option-sub">{c.subtitle}</span>}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {!lockClient && clientType !== 'none' && clientType !== 'recuperacao' && !selectedClient && (
          <p className="ccc-cal-hint">
            Associação por pesquisa disponível em Recuperação. Para {CCC_CLIENT_TYPE_LABELS[clientType]}, use o título e
            notas; a ligação à página será aberta pela área escolhida.
          </p>
        )}
        {startLocal && endLocal && (
          <p className="ccc-cal-hint">
            {formatTimeLabel(fromDatetimeLocalValue(startLocal) || new Date())} –{' '}
            {formatTimeLabel(fromDatetimeLocalValue(endLocal) || new Date())}
          </p>
        )}
      </div>
    </Modal>
  );
};

export default CccCalendarEventModal;
